import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionTooltipWrapper } from "components/permissions/permissions";
import {
  Download,
  Edit,
  Eye,
  MoreVertical,
  Receipt,
  Settings2,
  Trash2,
} from "lucide-react";
import { useHistory } from "react-router-dom";
import * as Routes from "routes";
import { openDownloadLinkCallback } from "utils/download";
import { hasPremiumData } from "../clip-ui/clip-menu";
import { isViewOnly } from "../clip-utils";
import { ClipConfiguration, Status } from "../clip-ui/models";

interface RowActionsMenuProps {
  clip: ClipConfiguration;
  nonEditableStatuses: Status[];
  onDelete: (clip: ClipConfiguration) => void;
}

/**
 * Context menu for clip row actions (View, Download, Edit, Invoice, Delete)
 */
export const RowActionsMenu = ({
  clip,
  onDelete,
}: RowActionsMenuProps) => {
  const history = useHistory();
  const viewOnly = isViewOnly(clip.status);
  const clipHasPremiumData = hasPremiumData(clip);
  const canDeleteClip =
    clip.status !== "Processing" && clip.status !== "Quoting";
  const downloadUrl = `${import.meta.env.VITE_DELIVERY_API_URL}/clip/${clip.id}/output`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenuItem
          onClick={() => {
            history.push(`${Routes.clipUi}?clip_id=${clip.id}`);
          }}
        >
          {viewOnly ? <Eye /> : <Edit />}
          {viewOnly ? "View" : "Edit"}
        </DropdownMenuItem>

        {clipHasPremiumData ? (
          <PermissionTooltipWrapper
            permissionKeys={[PermissionsKeys.premiumDataRead]}
            isDisabled={clip.status !== "Completed"}
          >
            <DropdownMenuItem onClick={openDownloadLinkCallback(downloadUrl)}>
              <Download />
              Download
            </DropdownMenuItem>
          </PermissionTooltipWrapper>
        ) : (
          <DropdownMenuItem
            onClick={openDownloadLinkCallback(downloadUrl)}
            disabled={clip.status !== "Completed"}
          >
            <Download />
            Download
          </DropdownMenuItem>
        )}

        <DropdownMenuItem
          onClick={() => {
            if (clip.invoiceUrl) {
              window.open(clip.invoiceUrl, "_blank");
            }
          }}
          disabled={!clip.invoiceId}
        >
          <Receipt />
          Invoice
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <PermissionTooltipWrapper
          permissionKeys={[PermissionsKeys.clipDelete]}
          isDisabled={!canDeleteClip}
        >
          <DropdownMenuItem
            variant="destructive"
            onClick={() => onDelete(clip)}
          >
            <Trash2 />
            Delete
          </DropdownMenuItem>
        </PermissionTooltipWrapper>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface ColumnVisibilityMenuProps {
  columnVisibility: Record<string, boolean>;
  onToggle: (column: string) => void;
}

/**
 * Menu for toggling column visibility
 */
export const ColumnVisibilityMenu = ({
  columnVisibility,
  onToggle,
}: ColumnVisibilityMenuProps) => {
  const columns = [
    { key: "name", label: "Name" },
    { key: "clientReference", label: "Client Ref" },
    { key: "totalCredits", label: "Credits" },
    { key: "totalDollars", label: "Cost ($)" },
    { key: "status", label: "Status" },
    { key: "premium", label: "Premium" },
    { key: "author", label: "Author" },
    { key: "lastEditedAt", label: "Modified" },
    { key: "purchasedOn", label: "Purchased" },
    { key: "completedOn", label: "Completed" },
    { key: "actions", label: "Actions" },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="default" className="shrink-0">
          <Settings2 />
          Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {columns.map(({ key, label }) => (
          <DropdownMenuCheckboxItem
            key={key}
            checked={columnVisibility[key]}
            onCheckedChange={() => onToggle(key)}
            onSelect={(e) => e.preventDefault()}
          >
            {label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
